import React, { useRef, useEffect, useState } from 'react';
import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import clx from 'clsx';
import { TextField } from 'components/FormFields';
import { FETCH_STATE } from 'constants';
import { partnersActions } from 'state/modules/partners';
import { Grid, Button } from '@material-ui/core';
import { useStyles } from './Common.styled';
import { PhoneField } from 'components/FormFields';

const partnerInviteSchema = () =>
  Yup.object().shape({
    meta_data: Yup.object().shape({
      org_name: Yup.string().required(i18n.t('common.required_field')),
      contact_name: Yup.string().required(i18n.t('common.required_field')),
      contact_email: Yup.string().email().required(i18n.t('common.required_field')),
      contact_phone: Yup.string()
        .min(4, i18n.t('common.required_field')) //Account for the country prefix
        .required(i18n.t('common.required_field')),
    }),
  });

const initialValues = () => ({
  partnership_type: 'FLEETSHARING',
  meta_data: {
    org_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  },
});

const InvitePartnerModal = ({ open, handleCancel }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const invitePartner = useSelector((state) => state.partners.invitePartner);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const validationSchema = partnerInviteSchema();
  const classes = useStyles();

  const loading = invitePartner === FETCH_STATE.LOADING;

  const handleSubmit = (values) => {
    dispatch(partnersActions.invitePartner(values));
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (invitePartner === FETCH_STATE.DONE) {
      setShowConfirmation(true);
    }
  }, [invitePartner]); // eslint-disable-line

  const handleConfirm = () => {
    dispatch(partnersActions.resetPartner());
    handleClose();
  };

  const handleClose = () => {
    handleCancel();
    setShowConfirmation(false);
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClose}
      enableCloseButton={false}
      handleClick={handleClose}
      enterButtonLabel={i18n.t('partners.invite')}
      loading={loading}
      title={i18n.t('partners.invite_title')}
      maxWidth="454px"
      hasFooter={!showConfirmation}
      hasHeader={!showConfirmation}
      containerClassName={classes.inviteContainer}
    >
      {!showConfirmation ? (
        <Formik
          innerRef={formRef}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          initialValues={initialValues()}
          validationSchema={validationSchema}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Grid container>
                <Grid item xs={12} style={{ marginBottom: '1.4rem' }}>
                  <span>{i18n.t('partners.invite_description')}</span>
                </Grid>
                <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                  <Field
                    margin="none"
                    name="meta_data.org_name"
                    component={TextField}
                    label={i18n.t('partners.partner_name')}
                    data-cy="partner-name-input"
                    required
                  />
                </Grid>
                <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                  <Field
                    margin="none"
                    name="meta_data.contact_name"
                    component={TextField}
                    label={i18n.t('partners.partner_contact')}
                    data-cy="partner-contact-input"
                    required
                  />
                </Grid>
                <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                  <Field
                    margin="none"
                    name="meta_data.contact_email"
                    component={TextField}
                    label={i18n.t('partners.partner_email')}
                    data-cy="partner-email-input"
                    required
                  />
                </Grid>
                <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                  <Field
                    margin="none"
                    name="meta_data.contact_phone"
                    component={PhoneField}
                    label={i18n.t('partners.partner_phone')}
                    data-cy="partner-name-input"
                    required
                    dialcodename="phone_code"
                    disableDropdown={false}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      ) : (
        <div className={clx(classes.confirmContainer, classes.confirmTop)}>
          <h1 className={classes.confirmTitle}>
            {i18n.t('partners.confirmation_title', {
              org: formRef.current?.values?.meta_data?.org_name,
            })}
          </h1>
          <span
            className={classes.confirmDescription}
            dangerouslySetInnerHTML={{ __html: i18n.t('partners.confirmation_description') }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            onClick={handleConfirm}
          >
            {i18n.t('common.ok')}
          </Button>
        </div>
      )}
    </BaseModal>
  );
};

export { InvitePartnerModal };
